import { useEffect, useState, useCallback, useId, useRef } from 'react';
import { ExternalLink, FileText, ImagePlus, RefreshCw, Trash2, UploadCloud } from 'lucide-react';
import { ImageCropper } from '../../shared/ui/ImageCropper';
import { cropImage } from '../../shared/utils/cropImage';
import './UploadPicture.css';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

const fileNameFromUrl = (url = '') => decodeURIComponent(url.split('?')[0].split('/').pop() || '');

export const UploadPicture = ({
  onSelect,
  id = '',
  noPreview = false,
  initialValue,
  noLabel,
  accept = 'image/*',
  label = null,
  textBtn,
  className = '',
  entityType = 'default',
  onRemove,
}) => {
  const [mainImg, setMainImg] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const autoId = useId();
  const inputId = `file-${id || autoId}`;
  const isImage = accept === 'image/*';
  const [showCropper, setShowCropper] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);

  // Определение пропорций кропа в зависимости от типа сущности
  const getAspectRatio = useCallback(() => {
    switch (entityType) {
      case 'news':
        return 16 / 9; // Новости - широкоформатные изображения
      case 'product':
        return 1; // Продукты - квадратные изображения
      case 'event':
        return 16 / 9; // События - широкоформатные изображения
      case 'portfolio':
        return 16 / 9; // Портфолио - широкоформатные изображения
      case 'vacancy':
        return 3 / 2; // Вакансии
      case 'partner':
        return 3 / 2; // Партнеры
      case 'team':
        return 3 / 4; // Команда - вертикальные изображения
      default:
        return 16 / 9; // По умолчанию - широкоформатные изображения
    }
  }, [entityType]);

  useEffect(() => {
    if (initialValue) {
      const formattedUrl = initialValue.startsWith('http') 
        ? initialValue 
        : `${import.meta.env.VITE_API_URL}${initialValue}`;
      
      setMainImg({ name: fileNameFromUrl(formattedUrl), value: formattedUrl, size: null, remote: true });
    } else {
      setMainImg(null);
    }
  }, [initialValue, accept]);

  const handleChange = (e) => {
    const file = e.target.files[0];
    // Тот же файл можно выбрать повторно
    e.target.value = '';
    if (!file) return;
    handleFile(file);
  };

  const handleFile = (file) => {
    
    // Сохраняем оригинальное имя файла
    const fileName = file.name || 'image.jpg';
    
    if (accept === 'image/*') {
      const imageUrl = URL.createObjectURL(file);
      setImageToEdit(imageUrl);
      
      // Сохраняем оригинальный файл с его именем
      const originalFileWithName = new File([file], fileName, { type: file.type });
      setOriginalFile(originalFileWithName);
      
      // Если нет необходимости в кропе, сразу отправляем файл
      if (noPreview) {
        setMainImg({ name: fileName, value: imageUrl, size: file.size });
        onSelect(originalFileWithName);
      } else {
        setShowCropper(true);
      }
    } else {
      // Для не-изображений
      const fileWithName = new File([file], fileName, { type: file.type });
      setMainImg({ name: fileName, value: URL.createObjectURL(fileWithName), size: file.size });
      onSelect(fileWithName);
    }
  };

  const handleCropComplete = async (cropResult) => {
    try {
      
      // Проверяем, что у нас есть изображение для кропа
      if (!imageToEdit) {
        console.error('Ошибка: изображение для кропа не определено');
        return;
      }

      // Проверяем формат результата кропа
      // Результат может быть либо объектом с параметрами кропа, либо объектом с blob
      if (cropResult.blob) {
        // Если получен blob напрямую из ImageCropper
        const fileName = originalFile?.name || 'cropped-image.jpg';
        const croppedFileWithName = new File([cropResult.blob], fileName, { 
          type: cropResult.blob.type || 'image/jpeg',
          lastModified: new Date().getTime()
        });

        setMainImg({
          name: fileName,
          value: cropResult.croppedImageUrl || URL.createObjectURL(croppedFileWithName),
          size: croppedFileWithName.size,
        });

        onSelect(croppedFileWithName);
      } else {
        // Если получены параметры кропа, используем cropImage
        if (!cropResult || !cropResult.width || !cropResult.height) {
          console.error('Ошибка: некорректные параметры кропа', cropResult);
          return;
        }

        const croppedFile = await cropImage(imageToEdit, cropResult);

        // Проверяем, что файл был успешно создан
        if (!croppedFile) {
          console.error('Ошибка: не удалось создать файл после кропа');
          return;
        }

        // Создаем новый файл с сохранением имени оригинального файла
        const fileName = originalFile?.name || 'cropped-image.jpg';
        const croppedFileWithName = new File([croppedFile], fileName, { 
          type: croppedFile.type || 'image/jpeg',
          lastModified: new Date().getTime()
        });

        setMainImg({ name: fileName, value: URL.createObjectURL(croppedFileWithName), size: croppedFileWithName.size });

        onSelect(croppedFileWithName);
      }
    } catch (error) {
      console.error('Ошибка при кропе изображения:', error);
      // Если есть оригинальный файл, используем его как запасной вариант
      if (originalFile) {
        setMainImg({ name: originalFile.name || '', value: URL.createObjectURL(originalFile), size: originalFile.size });
        onSelect(originalFile);
      }
    }
  };

  const openPicker = () => inputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (!isImage || file.type.startsWith('image/'))) handleFile(file);
  };

  const dragProps = {
    onDragOver: (e) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: handleDrop,
  };

  const handleRemove = () => {
    setMainImg(null);
    onRemove?.();
  };

  const showPreview = isImage && !noPreview && mainImg;

  return (
    <div className={`upload-picture mb-3 ${className}`}>
      {!noLabel && label && <div className="form-label">{label}</div>}

      <input ref={inputRef} id={inputId} type="file" accept={accept} hidden onChange={handleChange} />

      {!mainImg && (
        <button
          type="button"
          className={`upload-picture__drop ${dragOver ? 'is-dragover' : ''}`}
          style={isImage ? { aspectRatio: getAspectRatio() } : undefined}
          onClick={openPicker}
          {...dragProps}
        >
          {isImage ? <ImagePlus size={28} strokeWidth={1.6} /> : <UploadCloud size={28} strokeWidth={1.6} />}
          <span className="upload-picture__drop-title">{textBtn || (isImage ? 'Загрузить изображение' : 'Загрузить файл')}</span>
          <span className="upload-picture__drop-hint">Нажмите или перетащите файл сюда</span>
        </button>
      )}

      {mainImg && (
        <div className={`upload-picture__card ${dragOver ? 'is-dragover' : ''}`} {...dragProps}>
          {showPreview && (
            <a
              className="upload-picture__preview"
              style={{ aspectRatio: getAspectRatio() }}
              href={mainImg.value}
              target="_blank"
              rel="noreferrer"
              title="Открыть в полном размере"
            >
              <img src={mainImg.value} alt={mainImg.name || 'Изображение'} />
            </a>
          )}

          <div className="upload-picture__bar">
            {!isImage && <FileText size={20} className="upload-picture__file-icon" />}
            <div className="upload-picture__meta">
              <span className="upload-picture__name" title={mainImg.name}>
                {mainImg.name || (isImage ? 'Изображение' : 'Файл')}
              </span>
              <span className="upload-picture__info">
                {mainImg.remote ? 'Загружено' : formatSize(mainImg.size) || 'Новый файл'}
                {!mainImg.remote && <span className="upload-picture__badge">не сохранено</span>}
              </span>
            </div>
            <div className="upload-picture__actions">
              <button type="button" className="upload-picture__action" onClick={openPicker} title="Заменить">
                <RefreshCw size={16} />
              </button>
              {mainImg.remote && (
                <a className="upload-picture__action" href={mainImg.value} target="_blank" rel="noreferrer" title="Открыть">
                  <ExternalLink size={16} />
                </a>
              )}
              {onRemove && (
                <button type="button" className="upload-picture__action is-danger" onClick={handleRemove} title="Убрать">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Компонент кроппера изображений */}
      <ImageCropper
        image={imageToEdit}
        show={showCropper}
        onHide={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
        aspectRatio={getAspectRatio()}
      />
    </div>
  );
};
